// services/rtuAdapter.js
// RTU 状态/控制页的数据适配层。
// 视图只依赖这里导出的接口（getUnits / getRealtime / subscribe / getHistory /
// writeVars / sendVars / getSchedule / writeSchedule / getFleet）。
// 所有方法第一个参数为 unitId（mock 忽略）。
//
// 多从机（一台 DTU 挂多台 Digi-RTU）模型：
//   “unit” = 一台逻辑 RTU。数据来源 api/device/tree（type 3 = 从机节点，
//   携带 slaveId / slaveAddress / templateId；deviceCode 为所属 DTU 的编码）。
//   unitId = `${deviceCode}@${slaveAddress}`；无从机信息时退化为 deviceCode。
//
//   由于 device_realtime_var 以 (device_code, var_name) 唯一，多从机时每台
//   RTU 的通讯变量名必须各不相同（如 1A101 / 2A101）。视图内部仍统一使用
//   逻辑点名（room_temp / sp_occ_heat / ...，见数据点清单 85 项）；
//   本层通过 api/variable/list（按 template_id + slave_id 查询）建立
//   逻辑点名 ↔ 本从机通讯名 的映射：优先匹配 var_flag（变量标识），
//   var_flag 未填时按通讯名兜底 —— 因此模板中每个变量的 var_flag
//   应填写数据点清单中的逻辑点名。
//
//   兼容旧服务端（无 device/tree 接口，一台设备即一台 RTU）：自动回退到
//   扁平设备列表 + 恒等映射，行为与原实现一致。
//
// live 实现（Phase C）：realtimelist + SignalR(subscriptionHub, 轮询兜底) +
// historylistwhenall(pako解压) + cmd(写入后读回确认)。
// demoMode：localStorage['rtuDemoMode'] === '1' 时使用 mock（销售演示）。默认 live。

import pako from 'pako';
import * as signalR from '@microsoft/signalr';
import {
  getDevices as apiDeviceList,
  getDeviceTree,
  getRealtimeList,
  rollCall,
  queryHistoryWhenAllData,
  queryServiceTime,
} from '@/api/DeviceManagement';
import { getVariablesByTemplate } from '@/api/TemplateManagement';
import { getToken } from '@/utils/auth';
import { getConfig } from '@/utils/configTool';
import alarmMap from '@/config/rtuAlarmMap.json';
import registerMap from '@/config/rtuRegisterMap.json';

const TICK_MS = 5000;          // 网关上报周期
const POLL_FALLBACK_MS = 10000; // SignalR 不可用时的轮询兜底
const READBACK_DELAY_MS = 2500; // cmd 写入后读回等待
// 服务端订阅约 10 分钟未续订即释放、停止下发（张之伦 2026-07-21）——
// 定期重发 RequestData 续订，取一半周期留余量
const RESUBSCRIBE_MS = 5 * 60 * 1000;
const DAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const isDemoMode = () => localStorage.getItem('rtuDemoMode') === '1';

/* ------------------------------------------------------------------ */
/* helpers                                                             */
/* ------------------------------------------------------------------ */

const hm = (h, m = 0) => (h << 8) | m;
const packTime = (str) => {
  if (!str) return 0;
  const [h, m] = str.split(':').map(Number);
  return hm(h, m);
};
const unpackTime = (v) => {
  if (!v) return null;
  return `${String(v >> 8).padStart(2, '0')}:${String(v & 0xff).padStart(2, '0')}`;
};
// 服务端历史时间戳为 FILETIME 编码的“服务器本地时间”（无时区）。
// 统一约定：解码后按本地 Date 表示服务器钟面时间，与 update_at 字符串一致。
const FILETIME_EPOCH_DIFF = 11644473600000;
const fileTimeToDate = (ft) => {
  const d = new Date(ft / 10000 - FILETIME_EPOCH_DIFF);
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(),
    d.getUTCHours(), d.getUTCMinutes(), d.getUTCSeconds());
};
// 无时区的 'YYYY-MM-DDTHH:mm:ss'（服务器按自身时区解释）
const naiveFmt = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}T` +
  `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;

function decompress(base64) {
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  return JSON.parse(pako.inflate(bytes, { to: 'string' }));
}

// 历史检索必须按天分段，否则服务端失败（张之伦 2026-07-28）；与管理端历史图
// （ClientProjectHisData 的 splitTimeRangeToDays）一致：≤1天单段，否则按自然日拆分。
function splitRangeToDays(start, end) {
  if (end - start <= 24 * 3600e3) return [[new Date(start), new Date(end)]];
  const ranges = [];
  const cur = new Date(start); cur.setHours(0, 0, 0, 0);
  while (cur <= end) {
    const dayStart = new Date(cur);
    const dayEnd = new Date(cur); dayEnd.setHours(23, 59, 59, 999);
    const s = dayStart < start ? new Date(start) : dayStart;
    const e = dayEnd > end ? new Date(end) : dayEnd;
    if (s <= e) ranges.push([s, e]);
    cur.setDate(cur.getDate() + 1);
  }
  return ranges;
}
// 每日采样上限：天数越多每日点数越少（对齐 calculateMaxPointsPerDay）。
function maxPointsPerDay(totalDays) {
  if (totalDays <= 1) return 2000;
  if (totalDays <= 3) return 1500;
  if (totalDays <= 8) return 1000;
  if (totalDays <= 15) return 500;
  if (totalDays <= 30) return 300;
  return 200;
}

/**
 * values 映射 → FDD 健康状态。每个指示灯对应一个或多个变量（rtuAlarmMap
 * 的 vars 数组）：任一存在且非 0 = 报警，全部为 0 = 正常，全部缺失 = 灰。
 */
function buildHealth(values) {
  const grade = (group) => {
    const out = {};
    Object.entries(group).forEach(([key, cfg]) => {
      const present = cfg.vars
        .map((n) => values[n])
        .filter((v) => v !== undefined && v !== null);
      out[key] = !present.length ? 'na'
        : (present.some((v) => Number(v)) ? 'alarm' : 'ok');
    });
    return out;
  };
  return { comfort: grade(alarmMap.comfort), status: grade(alarmMap.status) };
}

/* ------------------------------------------------------------------ */
/* 逻辑点名清单（与 RTU-View-Data-Points 85 项一致）                    */
/* ------------------------------------------------------------------ */

// 视图关心的实时变量（订阅用；一次性整体订阅，见 subscribe）
const LIVE_VARS = [
  'room_temp', 'oat', 'sat', 'fan_hz', 'comp_hz', 'damper', 'power_kw',
  'fan_on', 'y1', 'y2', 'w1', 'w2', 'occupied', 'op_mode',
  'sp_occ_heat', 'sp_occ_cool', 'sp_unocc_heat', 'sp_unocc_cool',
  'fan_mode',
  ...Object.values(alarmMap.status).flatMap((c) => c.vars),
  ...Object.values(alarmMap.comfort).flatMap((c) => c.vars),
];
const SCHED_VARS = DAYS.flatMap((d) => [`sched_occs_${d}`, `sched_occe_${d}`, `sched_smart_${d}`]);
const HOLIDAY_VARS = Array.from({ length: 10 }, (_, i) => i + 1)
  .flatMap((n) => [`hd${n}_date`, `hd${n}_occs`, `hd${n}_occe`]);
// 本视图全部逻辑点名（var_flag / 通讯名兜底匹配白名单）
const LOGICAL_VARS = new Set([...LIVE_VARS, ...SCHED_VARS, ...HOLIDAY_VARS]);

// Modbus 寄存器地址 → 逻辑点名（长麟确认的 2C V4 地址表，见 rtuRegisterMap.json）
const REG_TO_LOGICAL = registerMap.registers;

// 变量识别方式（张之伦 2026-07-18 约定）：通讯名以十六进制寄存器地址结尾
// （如 1u330 → 0x330），按尾部地址识别逻辑点。
// 'suffix' = 启用该方案（当前）；'off' = 关闭，仅按 address_one/var_flag 匹配
//（旧方案）。如需回退只改这一个常量。
const WIRE_NAME_DECODE = 'suffix';

/**
 * 通讯名 → 逻辑点名。张之伦命名结构：从机序号(数字) + 类型字母 + 十六进制
 * 寄存器地址，如 3O25F / 3A143 / 1u330。必须按结构解析——不能简单取结尾的
 * 十六进制串：A 组名称整串都是十六进制字符（如 3A143），会被整体误读成地址。
 * 无法解析或地址未知返回 null（回退 address_one / var_flag 匹配）。
 */
function logicalFromWireName(name) {
  if (WIRE_NAME_DECODE !== 'suffix' || !name) return null;
  const m = /^\d+[A-Za-z]([0-9A-Fa-f]+)$/.exec(name);
  if (!m) return null;
  return REG_TO_LOGICAL[String(parseInt(m[1], 16))] ?? null;
}

/* ------------------------------------------------------------------ */
/* live adapter                                                        */
/* ------------------------------------------------------------------ */

// 恒等映射：旧服务端 / 未配置从机时，通讯名 = 逻辑点名
const IDENTITY_MAP = {
  identity: true,
  toComm: (n) => n,
  toLogical: (n) => (LOGICAL_VARS.has(n) ? n : null),
  mults: {},
};

function createLiveAdapter() {
  const unitCache = new Map();   // unitId -> unit
  const varMapCache = new Map(); // unitId -> {identity, toComm, toLogical, mults}
  const valueCache = new Map();  // unitId -> {values, mults, ts}（逻辑点名键）
  let unitList = [];
  let unitsLoaded = false;

  const unitIdOf = (deviceCode, slaveAddress) =>
    slaveAddress == null ? deviceCode : `${deviceCode}@${slaveAddress}`;

  /**
   * 单位清单。优先 api/device/tree（从机粒度，DTU 对用户透明）；
   * 旧服务端无该接口时回退到扁平设备列表（一台设备 = 一台 RTU）。
   */
  async function fetchUnits(force = false) {
    if (unitsLoaded && !force) return unitList;
    let units = [];
    try {
      // 调试钩子：localStorage['rtuForceFlat']='1' 强制走旧版扁平设备列表回退
      if (localStorage.getItem('rtuForceFlat') === '1') throw new Error('rtuForceFlat');
      // companyId 0 = 当前用户的组织（服务端按 ancestors 返回本组织及子组织）
      const res = await getDeviceTree({ companyId: 0, name: '', deviceCode: '', pageNum: 1, pageSize: 500 });
      if (!res?.success) throw new Error(res?.msg || 'device tree failed');
      const walk = (nodes, dtu) => {
        (nodes || []).forEach((node) => {
          if (node.type === 2) {
            const me = {
              deviceCode: node.deviceCode, name: node.name,
              companyId: node.companyId, gatewayName: node.gatewayName,
            };
            if ((node.children || []).some((c) => c.type === 3)) {
              walk(node.children, me);
            } else {
              // 未配置从机的 DTU：整机视作一台 RTU（恒等映射）
              units.push({
                unitId: node.deviceCode, name: node.name, deviceCode: node.deviceCode,
                companyId: node.companyId, slaveId: null, slaveAddress: null,
                templateId: null, dtuName: null, gatewayName: node.gatewayName,
              });
            }
          } else if (node.type === 3 && dtu) {
            units.push({
              unitId: unitIdOf(node.deviceCode || dtu.deviceCode, node.slaveAddress),
              name: node.name,
              deviceCode: node.deviceCode || dtu.deviceCode,
              companyId: node.companyId ?? dtu.companyId,
              slaveId: node.id,
              slaveAddress: node.slaveAddress,
              templateId: node.templateId,
              dtuName: dtu.name,
              gatewayName: dtu.gatewayName,
            });
          } else {
            walk(node.children, dtu); // 企业节点等：下钻
          }
        });
      };
      walk(res.data?.list || [], null);
    } catch (e) {
      console.warn('[rtu] device tree unavailable, flat device list fallback:', e?.message);
      const res = await apiDeviceList({ companyId: 0, pageNum: 1, pageSize: 200 });
      if (!res?.success) throw new Error(res?.msg || 'device list failed');
      units = (res.data.list || [])
        .filter((d) => d.state === 1 || d.state === 0)
        .map((d) => ({
          unitId: d.device_code, name: d.name, deviceCode: d.device_code,
          companyId: d.company_id, slaveId: null, slaveAddress: null,
          templateId: null, dtuName: null, gatewayName: null,
        }));
    }
    unitCache.clear();
    units.forEach((u) => unitCache.set(u.unitId, u));
    unitList = units;
    unitsLoaded = true;
    return units;
  }

  async function requireUnit(unitId) {
    if (!unitCache.has(unitId)) await fetchUnits();
    const u = unitCache.get(unitId);
    if (!u) throw new Error(`unknown RTU unit: ${unitId}`);
    return u;
  }

  /**
   * 本从机的 逻辑点名 ↔ 通讯名 映射（api/variable/list，按模板+从机查询）。
   * 识别优先级（张之伦 2026-07-18 约定）：
   *   1. 通讯名尾部十六进制地址（1u330 → 0x330，见 WIRE_NAME_DECODE）
   *   2. address_one（本地地址表 rtuRegisterMap.json）
   *   3. var_flag = 逻辑点名（兜底）
   *   4. 通讯名本身 = 逻辑点名（旧演示数据兜底）
   * 推送/实时数据仍通过本映射翻译（映射按从机建立，天然隔离同 DTU 其它从机）。
   */
  async function getVarMap(unitId) {
    if (varMapCache.has(unitId)) return varMapCache.get(unitId);
    const u = await requireUnit(unitId);
    let map = IDENTITY_MAP;
    if (u.templateId && u.slaveId) {
      try {
        const res = await getVariablesByTemplate({
          template_id: u.templateId, slave_id: u.slaveId, pageNum: 1, pageSize: 1000,
        });
        if (!res?.success) throw new Error(res?.msg || 'variable list failed');
        const toComm = new Map();
        const toLogical = new Map();
        const mults = {};
        (res.data?.list || []).forEach((row) => {
          const logical = logicalFromWireName(row.name)
            ?? REG_TO_LOGICAL[String(row.address_one)]
            ?? (LOGICAL_VARS.has(row.var_flag) ? row.var_flag : null)
            ?? (LOGICAL_VARS.has(row.name) ? row.name : null);
          if (!logical || toComm.has(logical)) return;
          toComm.set(logical, row.name);
          toLogical.set(row.name, logical);
          if (row.mult) mults[logical] = row.mult;
        });
        map = {
          identity: false,
          toComm: (n) => toComm.get(n) ?? null,
          toLogical: (n) => toLogical.get(n) ?? null,
          mults,
        };
      } catch (e) {
        console.warn('[rtu] variable map load failed, identity mapping:', e?.message);
      }
    }
    varMapCache.set(unitId, map);
    return map;
  }

  /**
   * realtimelist 返回整台 DTU（所有从机）的行；按映射摘出本从机的逻辑值。
   * 倍率约定（张之伦 2026-07-19）：服务端返回寄存器原始值 + mult，
   * 显示值 = 原始值 × mult（SignalR 推送同样处理，见 subscribe）；
   * 写入相反：显示值 ÷ mult。mult 未设时按 1（演示库不受影响）。
   * 注意：他的 mult 相关服务端改动仍在测试中——落地后需回归验证一次。
   */
  function pickUnitRows(rows, u, vm) {
    const values = {};
    const mults = {};
    let ts = null;
    let imei = null;
    rows.forEach((row) => {
      if (row.device_code !== u.deviceCode) return;
      // 网关 imei：cmd 下发接口要求（张之伦测试服校验 Imei 必填）；从实时行捕获
      if (!imei && row.gateway_imei) imei = row.gateway_imei;
      const logical = vm.toLogical(row.var_name);
      if (!logical) return;
      const mult = row.mult || 1;
      values[logical] = row.current_value == null ? row.current_value : row.current_value * mult;
      mults[logical] = mult;
      const t = new Date(row.update_at);
      if (!ts || t > ts) ts = t;
    });
    return { values, mults, ts, imei };
  }

  async function fetchRealtime(unitId) {
    const u = await requireUnit(unitId);
    const vm = await getVarMap(unitId);
    const res = await getRealtimeList([{
      company_id: u.companyId ?? 1,
      device_code: u.deviceCode,
      pageNum: 1, pageSize: 500,
    }]);
    if (!res?.success) throw new Error(res?.msg || 'realtimelist failed');
    const { values, mults, ts, imei } = pickUnitRows(res.data.list || [], u, vm);
    if (imei) u.imei = imei; // 供 cmd 下发使用（同 DTU 各从机共享网关 imei）
    valueCache.set(unitId, { values, mults: { ...vm.mults, ...mults }, ts: ts || new Date() });
    return snapshotOf(unitId);
  }

  function snapshotOf(unitId) {
    const c = valueCache.get(unitId) || { values: {}, ts: new Date() };
    return { values: { ...c.values }, health: buildHealth(c.values), ts: c.ts };
  }

  // cmd 下发需要网关 imei（张之伦测试服 Imei 必填）。优先用已捕获的；
  // 缺失时（如分组联动写到未打开过的从机）拉一次实时数据补齐。
  async function ensureImei(u, unitId) {
    if (u.imei) return u.imei;
    try { await fetchRealtime(unitId); } catch (e) { /* 离线设备无从获取 */ }
    return u.imei || undefined;
  }

  return {
    demo: false,

    /** [{unitId, name, deviceCode, dtuName, slaveAddress, gatewayName}] */
    getUnits: fetchUnits,

    async getRealtime(unitId) {
      return fetchRealtime(unitId);
    },

    /**
     * 主动点名刷新：下发 rollCall 读请求（协议限制每包≤5个寄存器，分组下发），
     * 强制网关立即回读本从机寄存器并上报，随后重新拉取最新值返回快照。
     * 用于“刷新”按钮——被动订阅上报稀疏时，用户可按需强制刷新（对齐管理端刷新）。
     */
    async refresh(unitId) {
      const u = await requireUnit(unitId);
      const vm = await getVarMap(unitId);
      const commVars = [...new Set(LIVE_VARS.map((n) => vm.toComm(n)).filter(Boolean))];
      if (commVars.length) {
        const imei = await ensureImei(u, unitId);
        const chunks = [];
        for (let i = 0; i < commVars.length; i += 5) chunks.push(commVars.slice(i, i + 5));
        for (const chunk of chunks) {
          try {
            await rollCall([{
              deviceCode: u.deviceCode, imei, Dir: 'Down', type: 4,
              timer: new Date().toISOString(), priority: 1,
              r_data: chunk.map((name) => ({ name })), w_data: [],
            }]);
          } catch (e) { /* 单包失败不影响其它包 */ }
          await new Promise((r) => setTimeout(r, 100));
        }
        // 给网关回读留时间再拉取（真实网关秒级~分钟级不等）
        await new Promise((r) => setTimeout(r, READBACK_DELAY_MS));
      }
      return fetchRealtime(unitId);
    },

    /**
     * 概览：每台 DTU 一次 realtimelist，按从机映射拆分。
     * 返回 [{unitId, deviceCode, name, dtuName, values, health, hasHeater, hasData, ts}]
     */
    async getFleet() {
      const units = await fetchUnits();
      if (!units.length) return [];
      await Promise.all(units.map((u) => getVarMap(u.unitId).catch(() => {})));
      const byDevice = new Map();
      units.forEach((u) => {
        if (!byDevice.has(u.deviceCode)) byDevice.set(u.deviceCode, []);
        byDevice.get(u.deviceCode).push(u);
      });
      const results = new Map();
      // realtimelist 不支持多设备合并查询（多元素时返回空）——并行逐台 DTU 查询
      await Promise.all([...byDevice.entries()].map(async ([code, group]) => {
        let rows = [];
        try {
          const res = await getRealtimeList([{
            company_id: group[0].companyId ?? 1,
            device_code: code,
            pageNum: 1, pageSize: 500,
          }]);
          rows = res?.data?.list || [];
        } catch (e) { /* 单台失败不影响其它 */ }
        group.forEach((u) => {
          const vm = varMapCache.get(u.unitId) || IDENTITY_MAP;
          const { values, ts, imei } = pickUnitRows(rows, u, vm);
          if (imei) u.imei = imei; // 概览也捕获网关 imei，供后续 cmd 下发
          results.set(u.unitId, {
            unitId: u.unitId,
            deviceCode: u.deviceCode,
            name: u.name,
            dtuName: u.dtuName,
            values,
            health: buildHealth(values),
            hasHeater: 'w1' in values,
            // 上报了任一本视图变量即算有数据（勿只看 room_temp——有的机组不上报
            // 室温但上报运行模式/在室/报警等，仪表盘不应显示“无数据”，张之伦 2026-07-22）
            hasData: Object.keys(values).length > 0,
            ts,
          });
        });
      }));
      return units.map((u) => results.get(u.unitId)).filter(Boolean);
    },

    /**
     * SignalR 推送（subscriptionHub, ReceiveMessage）为主，轮询兜底。
     * 订阅/推送均使用本从机的通讯名，回调值已翻译回逻辑点名。
     * 返回取消订阅函数。
     */
    subscribe(unitId, cb) {
      let closed = false;
      let connection = null;
      let u = null;
      let commVars = [];
      let heartbeatTimer = null;

      const pollTimer = setInterval(async () => {
        if (closed) return;
        try { cb(await fetchRealtime(unitId)); } catch (e) { /* 保持上次快照 */ }
      }, connectionAlive() ? POLL_FALLBACK_MS : TICK_MS);

      function connectionAlive() {
        return connection && connection.state === signalR.HubConnectionState.Connected;
      }

      // 一次性订阅全部变量（不能分批，服务端整体处理）
      async function sendRequestData() {
        await connection.invoke('RequestData', JSON.stringify({
          token: getToken(), deviceCode: u.deviceCode, imei: null, Dir: 'd', type: 1,
          timer: new Date().toISOString(), priority: 1,
          r_data: commVars.map((name) => ({ name })),
        }));
      }

      (async () => {
        let vm;
        try {
          u = await requireUnit(unitId);
          vm = await getVarMap(unitId);
        } catch (e) {
          console.warn('[rtu] subscribe: unit unavailable:', e?.message);
          return;
        }
        commVars = LIVE_VARS.map((n) => vm.toComm(n)).filter(Boolean);
        try {
          const config = await getConfig();
          const hubUrl = config.signalRUrl; // getConfig 已拼接 /subscriptionHub
          connection = new signalR.HubConnectionBuilder()
            .withUrl(hubUrl, { accessTokenFactory: () => getToken() || '' })
            .withAutomaticReconnect()
            .build();

          connection.on('ReceiveMessage', (message) => {
            if (closed) return;
            try {
              const data = JSON.parse(message);
              if (data.DeviceCode !== u.deviceCode || !Array.isArray(data.Vars)) return;
              const cache = valueCache.get(unitId) || { values: {}, mults: {}, ts: new Date() };
              let touched = false;
              data.Vars.forEach((v) => {
                const logical = v?.Name && vm.toLogical(v.Name);
                if (logical && (v.Error === 0 || v.Error === undefined)) {
                  // 推送值同为寄存器原始值：显示值 = 原始值 × mult
                  cache.values[logical] = Number(v.Value) * (cache.mults[logical] || vm.mults[logical] || 1);
                  touched = true;
                }
              });
              if (!touched) return; // 同 DTU 其它从机的推送
              // ts 必须保持“服务器钟面时间”（历史查询窗口依赖它），勿用浏览器时间
              if (data.time) cache.ts = new Date(String(data.time).replace('Z', ''));
              valueCache.set(unitId, cache);
              cb(snapshotOf(unitId));
            } catch (e) { console.warn('[rtu] push parse failed', e); }
          });

          await connection.start();
          await sendRequestData();
          console.log('[rtu] SignalR subscribed', unitId, `(${commVars.length} vars)`);
          // 心跳续订：服务端约 10 分钟未续订即释放订阅、停止下发，故定期重发
          heartbeatTimer = setInterval(async () => {
            if (closed || !connectionAlive()) return;
            try { await sendRequestData(); } catch (e) { console.warn('[rtu] resubscribe failed', e?.message); }
          }, RESUBSCRIBE_MS);
        } catch (e) {
          console.warn('[rtu] SignalR unavailable, polling only:', e?.message);
        }
      })();

      return () => {
        closed = true;
        clearInterval(pollTimer);
        if (heartbeatTimer) clearInterval(heartbeatTimer);
        if (connection) {
          const conn = connection;
          (async () => {
            try {
              if (conn.state === signalR.HubConnectionState.Connected) {
                await conn.invoke('ReleaseData', JSON.stringify({
                  token: getToken(), deviceCode: u?.deviceCode, imei: null, Dir: 'd', type: 1,
                  timer: new Date().toISOString(), priority: 1,
                  r_data: commVars.map((name) => ({ name })),
                }));
              }
            } catch (e) { /* best effort */ }
            conn.stop().catch(() => {});
          })();
        }
      };
    },

    /** rangeHours: 24 | 168 | 720 */
    async getHistory(unitId, rangeHours) {
      const u = await requireUnit(unitId);
      const vm = await getVarMap(unitId);
      // 查询窗口按“服务器钟面时间”表达：以最近 update_at 为服务器当前时间
      if (!valueCache.has(unitId)) await this.getRealtime(unitId);
      const serverNow = valueCache.get(unitId)?.ts || new Date();
      const end = new Date(serverNow.getTime() + 60e3);
      const start = new Date(end.getTime() - rangeHours * 3600e3);
      const fmt = naiveFmt;
      const laneVars = ['fan_on', 'y1', 'w1', 'occupied'];
      const alarmVars = Object.values(alarmMap.status).flatMap((c) => c.vars);
      const varNames = ['room_temp', 'oat', 'sat', 'sp_occ_heat', 'sp_unocc_heat', ...laneVars, ...alarmVars];

      // 查询条件需带 mult（张之伦 2026-07-20：服务端按它换算历史原始值）
      const varSpec = varNames
        .map((n) => ({ comm: vm.toComm(n), mult: vm.mults[n] || 1 }))
        .filter((x) => x.comm)
        .map((x) => ({ var_name: x.comm, var_decimal: 2, mult: x.mult }));
      // 历史必须按天分段检索（张之伦 2026-07-28）：7天=逐日7次并行，再合并。
      const dayRanges = splitRangeToDays(start, end);
      const maxPts = maxPointsPerDay(dayRanges.length);
      let anyOk = false;
      const perDay = await Promise.all(dayRanges.map(([ds, de]) =>
        queryHistoryWhenAllData({
          company_id: u.companyId ?? 1,
          device_code: u.deviceCode,
          maxPoints: maxPts,
          var_names: varSpec,
          ben_time: fmt(ds), end_time: fmt(de),
          pageNum: 1, pageSize: 100000,
        }).then((res) => {
          if (!res?.success) return [];
          anyOk = true;
          return res.data.compressed ? decompress(res.data.compressedData) : (res.data.list || []);
        }).catch(() => [])
      ));
      if (!anyOk) throw new Error('history failed');
      // 合并各天分组：按通讯名聚合 list（下游 seriesOf 会按时间排序，天序无关）
      const mergedByComm = new Map();
      perDay.flat().forEach((g) => {
        if (!g || !g.var_name) return;
        if (!mergedByComm.has(g.var_name)) mergedByComm.set(g.var_name, { ...g, list: [] });
        const dst = mergedByComm.get(g.var_name);
        (g.list || []).forEach((p) => dst.list.push(p));
      });
      // 通讯名 → 逻辑点名
      const groups = [...mergedByComm.values()].map((g) => ({ ...g, var_name: vm.toLogical(g.var_name) || g.var_name }));

      const seriesOf = (name) => {
        const g = groups.find((x) => x.var_name === name);
        return (g?.list || [])
          .filter((p) => p.error_code === 0)
          .map((p) => ({ t: fileTimeToDate(p.timestamp), v: p.var_value }))
          .sort((a, b) => a.t - b.t);
      };

      // 温度与“当前生效设定值”（有人=occ、无人=unocc，按 occupied 序列切换）
      const temp = seriesOf('room_temp');
      const occSeries = seriesOf('occupied');
      const occHeat = seriesOf('sp_occ_heat');
      const unoccHeat = seriesOf('sp_unocc_heat');
      const lastAt = (series, t, dflt) => {
        let val = dflt;
        for (const p of series) { if (p.t <= t) val = p.v; else break; }
        return val;
      };
      const oatSeries = seriesOf('oat');
      const satSeries = seriesOf('sat');
      // 时间轴基准：优先 room_temp；机组未上报室温历史时退而用 OAT/SAT，
      // 有什么画什么（张之伦 2026-07-20：部分测试机组无室温也应显示曲线）
      const axisSeries = temp.length ? temp : (oatSeries.length ? oatSeries : satSeries);
      const points = axisSeries.map((p) => ({
        t: p.t,
        temp: lastAt(temp, p.t, null),
        sp: lastAt(occSeries, p.t, 1)
          ? lastAt(occHeat, p.t, null)
          : lastAt(unoccHeat, p.t, null),
        oat: lastAt(oatSeries, p.t, null),
        sat: lastAt(satSeries, p.t, null),
      }));

      // 0/1 序列 → 时间带
      const toBands = (series) => {
        const bands = [];
        let openAt = null;
        series.forEach((p) => {
          if (Number(p.v) && openAt === null) openAt = p.t.getTime();
          if (!Number(p.v) && openAt !== null) { bands.push([openAt, p.t.getTime()]); openAt = null; }
        });
        if (openAt !== null) bands.push([openAt, end.getTime()]);
        return bands;
      };
      const lanes = [
        { key: 'fan', bands: toBands(seriesOf('fan_on')) },
        { key: 'compressor', bands: toBands(seriesOf('y1')) },
        { key: 'heat', bands: toBands(seriesOf('w1')) },
        { key: 'occupied', bands: toBands(occSeries) },
      ];

      // 报警变量 0→非0 沿 = 故障事件（指示灯的任一变量报警都计入该指示灯）
      const faults = [];
      Object.entries(alarmMap.status).forEach(([key, cfg]) => {
        cfg.vars.forEach((varName) => {
          let prev = 0;
          seriesOf(varName).forEach((p) => {
            if (Number(p.v) && !prev) faults.push({ t: p.t, key });
            prev = Number(p.v);
          });
        });
      });
      faults.sort((a, b) => a.t - b.t);

      return { points, lanes, faults };
    },

    /**
     * writes: [{name, value}]（逻辑点名，显示单位）。翻译为本从机通讯名、
     * 按 mult 换算下发。cmd 受理即视为写入成功（真实网关按自身周期回读
     * 寄存器，回显可能远超本函数的等待窗口——张之伦 2026-07-20 实测）。
     * 读回仅作确认：返回 [{name, success, confirmed, value}]，
     * confirmed=false 表示已下发但设备尚未回报新值。
     */
    async writeVars(unitId, writes) {
      const u = await requireUnit(unitId);
      const vm = await getVarMap(unitId);
      const mults = { ...vm.mults, ...(valueCache.get(unitId)?.mults || {}) };
      const mapped = writes.filter((w) => vm.toComm(w.name));
      const unmapped = writes.filter((w) => !vm.toComm(w.name));
      if (unmapped.length) {
        console.warn('[rtu] writeVars: no comm name for', unmapped.map((w) => w.name));
      }
      // reason 区分“该机组未映射该变量（配置问题，需查模板映射）”与
      // “下发失败（通讯错误）”，供 UI 给出不同提示（张之伦 2026-07-20）
      let results = unmapped.map(({ name }) => ({ name, success: false, confirmed: false, value: null, reason: 'unmapped' }));
      if (!mapped.length) return results;

      const imei = await ensureImei(u, unitId);
      const res = await rollCall([{
        deviceCode: u.deviceCode, imei, Dir: 'Down', type: 4,
        timer: new Date().toISOString(), priority: 1,
        r_data: mapped.map(({ name }) => ({ name: vm.toComm(name) })),
        w_data: mapped.map(({ name, value }) => ({ name: vm.toComm(name), value: value / (mults[name] || 1) })),
      }]);
      if (!res?.success) {
        return results.concat(mapped.map(({ name }) => ({ name, success: false, confirmed: false, value: null, reason: 'send_failed' })));
      }
      // 读回确认（尽力而为）：模拟器秒级回显；真实网关可能需数分钟
      const matches = (snap) => mapped.every(({ name, value }) => {
        const actual = snap.values[name];
        return actual !== undefined && Math.abs(Number(actual) - Number(value)) < 0.11;
      });
      let snap = null;
      for (let attempt = 0; attempt < 5; attempt++) {
        await new Promise((r) => setTimeout(r, attempt === 0 ? READBACK_DELAY_MS : 2000));
        snap = await fetchRealtime(unitId);
        if (matches(snap)) break;
      }
      return results.concat(mapped.map(({ name, value }) => {
        const actual = snap.values[name];
        const ok = actual !== undefined && Math.abs(Number(actual) - Number(value)) < 0.11;
        return { name, success: true, confirmed: ok, value: ok ? actual : value };
      }));
    },

    /** 组内联动写入：只下发 cmd，不做读回确认（供分组传播用）。 */
    async sendVars(unitId, writes) {
      const u = await requireUnit(unitId);
      const vm = await getVarMap(unitId);
      const mults = { ...vm.mults, ...(valueCache.get(unitId)?.mults || {}) };
      const mapped = writes.filter((w) => vm.toComm(w.name));
      if (!mapped.length) return { success: false };
      const imei = await ensureImei(u, unitId);
      const res = await rollCall([{
        deviceCode: u.deviceCode, imei, Dir: 'Down', type: 4,
        timer: new Date().toISOString(), priority: 1,
        r_data: [],
        w_data: mapped.map(({ name, value }) => ({ name: vm.toComm(name), value: value / (mults[name] || 1) })),
      }]);
      return { success: !!res?.success };
    },

    async getSchedule(unitId) {
      const snap = await this.getRealtime(unitId);
      const v = snap.values;
      const days = DAYS.map((d) => ({
        day: d,
        start: unpackTime(v[`sched_occs_${d}`]),
        end: unpackTime(v[`sched_occe_${d}`]),
        smart: !!Number(v[`sched_smart_${d}`] || 0),
      }));
      // UI 周一开头
      const ordered = [...days.slice(1), days[0]];
      const holidays = [];
      for (let n = 1; n <= 10; n++) {
        const date = Number(v[`hd${n}_date`] || 0);
        if (date) {
          holidays.push({
            date: `${String(date >> 8).padStart(2, '0')}-${String(date & 0xff).padStart(2, '0')}`,
            label: `${MONTHS[(date >> 8) - 1] || '?'} ${date & 0xff}`,
          });
        }
      }
      return { days: ordered, holidays, maxHolidays: 10 };
    },

    /**
     * diff: { days: [{day,start,end,smart}] } — 仅写变化的寄存器。
     * unconfirmed = 已下发但设备尚未回报的变量（真实网关回读慢，不算失败）。
     */
    async writeSchedule(unitId, diff) {
      const writes = [];
      (diff.days || []).forEach((d) => {
        writes.push({ name: `sched_occs_${d.day}`, value: packTime(d.start) });
        writes.push({ name: `sched_occe_${d.day}`, value: packTime(d.end) });
        writes.push({ name: `sched_smart_${d.day}`, value: d.smart ? 1 : 0 });
      });
      const results = await this.writeVars(unitId, writes);
      const sendFailed = results.filter((r) => r.reason === 'send_failed');
      const unmapped = results.filter((r) => r.reason === 'unmapped');
      const unconfirmed = results.filter((r) => r.success && !r.confirmed);
      return {
        success: sendFailed.length === 0 && unmapped.length === 0,
        written: writes.length,
        failed: sendFailed.map((f) => f.name),
        unmapped: unmapped.map((f) => f.name),
        unconfirmed: unconfirmed.map((f) => f.name),
      };
    },

    /** 服务端当前时间（无时区“钟面时间”）。判断数据过期须用它而非客户端时钟——
     *  服务端与客户端可能相差多个时区，直接与本地 Date.now() 比较会错判。 */
    async getServerTime() {
      try {
        const res = await queryServiceTime({});
        const s = res?.data?.serviceTime;
        return s ? new Date(String(s).replace(' ', 'T')) : null;
      } catch (e) { return null; }
    },

    /** 该机组在 variable/list 中实际映射到的逻辑点名集合（供 UI 判断哪些可编辑）。 */
    async getMappedVars(unitId) {
      const vm = await getVarMap(unitId);
      const out = new Set();
      LOGICAL_VARS.forEach((n) => { if (vm.toComm(n)) out.add(n); });
      return out;
    },
  };
}

/* ------------------------------------------------------------------ */
/* mock adapter（演示模式；Phase A 原样保留，方法签名加 unitId）         */
/* ------------------------------------------------------------------ */

const mockState = {
  room_temp: 69.4, oat: 61.7, sat: 63.9,
  fan_hz: 48.42, comp_hz: 0.0, damper: 100.0, power_kw: 3.77,
  // op_mode（OMC 枚举）：1 消防锁定, 2 关, 3 通风, 4 制热, 5 制冷
  fan_on: 1, y1: 0, y2: 0, w1: 0, w2: 0, occupied: 1, op_mode: 3,
  sp_occ_heat: 68.0, sp_occ_cool: 74.0, sp_unocc_heat: 62.0, sp_unocc_cool: 80.0,
  fan_mode: 0, // 0 auto / 1 on
  alm_sens_sa: 0, alm_sens_ra: 1, alm_sens_oa: 0,
  alm_econ_open: 0, alm_econ_close: 0, alm_fan_coil: 0,
  alm_outdoor1: 0, alm_outdoor2: 0, alm_comp1: 0, alm_comp2: 0,
  alm_heat1: 0, alm_heat2: 0, alm_vfd: 0,
  st_energy: 0, st_demand: 0, st_cooling: 0, st_heating: 0,
};
// IAQ/湿度演示为“无数据”（不加入 state → na）

const mockSchedule = {
  days: [
    { day: 'mon', start: '06:00', end: '18:00', smart: true },
    { day: 'tue', start: '06:00', end: '18:00', smart: true },
    { day: 'wed', start: '06:00', end: '18:00', smart: true },
    { day: 'thu', start: '06:00', end: '18:00', smart: true },
    { day: 'fri', start: '06:00', end: '17:00', smart: true },
    { day: 'sat', start: null, end: null, smart: false },
    { day: 'sun', start: null, end: null, smart: false },
  ],
  holidays: [
    { date: '12-25', label: 'Dec 25' },
    { date: '01-01', label: 'Jan 1' },
  ],
  maxHolidays: 10,
};

let mockTimer = null;
const mockSubs = new Set();

function mockDrift(v, amp, min, max) {
  return Math.min(max, Math.max(min, v + (Math.random() - 0.5) * amp));
}
function mockTick() {
  mockState.room_temp = mockDrift(mockState.room_temp, 0.4, 66, 72);
  mockState.sat = mockDrift(mockState.sat, 0.6, 58, 70);
  mockState.oat = mockDrift(mockState.oat, 0.3, 55, 68);
  mockState.fan_hz = mockState.fan_on ? mockDrift(mockState.fan_hz, 1.2, 30, 60) : 0;
  mockState.power_kw = mockDrift(mockState.power_kw, 0.15, 2.8, 5.2);
  const snap = mockSnapshot();
  mockSubs.forEach((cb) => cb(snap));
}
function mockSnapshot() {
  return { values: { ...mockState }, health: buildHealth(mockState), ts: new Date() };
}
function mockGenHistory(rangeHours) {
  const pts = [];
  const stepH = rangeHours / 96;
  const now = Date.now();
  const startMs = now - rangeHours * 3600e3;
  const spFor = (d) => { const h = d.getHours() + d.getMinutes() / 60; return h >= 6 && h < 18 ? 68 : 62; };
  for (let i = 0; i <= 96; i++) {
    const t = new Date(startMs + i * stepH * 3600e3);
    const sp = spFor(t);
    const h = t.getHours() + t.getMinutes() / 60;
    const wobble = Math.sin(i / 5) * 0.7 + Math.sin(i / 2.1 + 1) * 0.3;
    let temp;
    if (h >= 6 && h < 7.5) temp = 62.5 + ((h - 6) / 1.5) * 6 + wobble * 0.3;
    else if (h >= 7.5 && h < 18) temp = 68.5 + wobble + (h > 13.8 && h < 15 ? 1.9 : 0);
    else temp = 62.8 + wobble * 0.6;
    pts.push({
      t, temp: +temp.toFixed(2), sp,
      oat: +(58 + Math.sin(i / 8) * 6 + wobble).toFixed(2),
      sat: +(temp - 4 + wobble).toFixed(2),
    });
  }
  const dayBands = (fromH, toH) => {
    const bands = [];
    const daysN = Math.ceil(rangeHours / 24);
    for (let d = 0; d <= daysN; d++) {
      const base = new Date(now); base.setHours(0, 0, 0, 0);
      const dayStart = base.getTime() - d * 24 * 3600e3;
      const a = dayStart + fromH * 3600e3, b = dayStart + toH * 3600e3;
      if (b > startMs && a < now) bands.push([Math.max(a, startMs), Math.min(b, now)]);
    }
    return bands.reverse();
  };
  const lanes = [
    { key: 'fan', bands: dayBands(5.75, 18.25) },
    { key: 'compressor', bands: dayBands(11.2, 12.1).concat(dayBands(13.0, 15.4)) },
    { key: 'heat', bands: dayBands(6.0, 7.6) },
    { key: 'occupied', bands: dayBands(6, 18) },
  ];
  const f = new Date(now); f.setHours(14, 5, 0, 0);
  const faults = f.getTime() > startMs && f.getTime() < now
    ? [{ t: f, key: 'tstat' }] : [];
  return { points: pts, lanes, faults };
}

const mockAdapter = {
  demo: true,
  async getUnits() {
    return [
      { unitId: 'SIM001', deviceCode: 'SIM001', name: 'RTU1 — Sim Device', dtuName: null, slaveAddress: null },
      { unitId: 'SIM002', deviceCode: 'SIM002', name: 'RTU2 — Sim Device', dtuName: null, slaveAddress: null },
    ];
  },
  async getRealtime() { return mockSnapshot(); },
  async refresh() { return mockSnapshot(); },
  async getFleet() {
    const mk = (code, name, over) => {
      const values = { ...mockState, ...over };
      return {
        unitId: code, deviceCode: code, name, dtuName: null, values, health: buildHealth(values),
        hasHeater: over.noHeater ? false : true, hasData: true, ts: new Date(),
      };
    };
    return [
      mk('SIM001', 'RTU1 — Sim Device', {}),
      mk('SIM002', 'RTU2 — Sim Device', { op_mode: 5, y1: 1, room_temp: 74.8 }),
      mk('SIM003', 'RTU3 — Sim Device', { op_mode: 4, w1: 1, room_temp: 64.1, alm_sens_ra: 0 }),
    ];
  },
  subscribe(_unitId, cb) {
    mockSubs.add(cb);
    if (!mockTimer) mockTimer = setInterval(mockTick, TICK_MS);
    return () => {
      mockSubs.delete(cb);
      if (mockSubs.size === 0 && mockTimer) { clearInterval(mockTimer); mockTimer = null; }
    };
  },
  async getHistory(_unitId, rangeHours) {
    await new Promise((r) => setTimeout(r, 250));
    return mockGenHistory(rangeHours);
  },
  async writeVars(_unitId, writes) {
    await new Promise((r) => setTimeout(r, 600));
    return writes.map(({ name, value }) => {
      if (name in mockState) mockState[name] = value;
      return { name, success: true, confirmed: true, value };
    });
  },
  async sendVars() { await new Promise((r) => setTimeout(r, 300)); return { success: true }; },
  async getMappedVars() { return new Set(LOGICAL_VARS); },
  async getServerTime() { return new Date(); },
  async getSchedule() { return JSON.parse(JSON.stringify(mockSchedule)); },
  async writeSchedule(_unitId, diff) {
    await new Promise((r) => setTimeout(r, 600));
    (diff.days || []).forEach((d) => {
      const row = mockSchedule.days.find((x) => x.day === d.day);
      if (row) Object.assign(row, d);
    });
    return { success: true, written: (diff.days || []).length, failed: [] };
  },
};

let liveInstance = null;
export function getRtuAdapter() {
  if (isDemoMode()) return mockAdapter;
  if (!liveInstance) liveInstance = createLiveAdapter();
  return liveInstance;
}
